/**
 * Formatting utilities for displaying values consistently across the application
 */

/**
 * Formats satoshis to a human-readable BTC value
 */
export function formatSats(sats: number): string {
  const btcValue = sats / 100_000_000;
  // Convert to string with fixed 8 decimals, then remove trailing zeros
  return btcValue.toFixed(8).replace(/\.?0+$/, '');
}

/**
 * Formats a token amount based on its decimals
 */
export function formatTokenAmount(amount: number, decimals: number | undefined): string {
  if (decimals === undefined) {
    return amount.toLocaleString(); // No decimal info, show raw amount
  }
  const value = amount / 10 ** decimals;

  // For millions and billions, use abbreviated format
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2).replace(/(\.\d*[1-9])0+$/, '$1')}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2).replace(/(\.\d*[1-9])0+$/, '$1')}M`;
  }

  // Format with maximum possible decimals
  const formatted = value.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  });

  // Remove trailing zeros after decimal point
  return formatted.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

/**
 * Formats a token's USD value with currency symbol
 */
export function formatTokenValue(value: number | undefined): string {
  if (value === undefined || value === null) return '$0.00';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * Truncates a long string (typically a pubkey or address) with ellipsis
 */
export function truncateKey(key: string | undefined, startLength = 8, endLength = 8): string {
  if (!key) return '';
  if (key.length <= startLength + endLength + 3) return key;
  return `${key.substring(0, startLength)}...${key.substring(key.length - endLength)}`;
}

/**
 * Formats an ISO timestamp to a localized datetime string
 */
export function formatTimestamp(timestamp: string | undefined): string {
  if (!timestamp) return 'N/A';
  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Formats an ISO timestamp as a relative time (e.g., "2 mins ago")
 */
export function formatRelativeTime(timestamp: string | undefined): string {
  if (!timestamp) return 'N/A';

  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Convert to seconds
    const diffSec = Math.floor(diffMs / 1000);

    // Less than a minute
    if (diffSec < 60) {
      return `${diffSec}s ago`;
    }

    // Less than an hour
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) {
      return `${diffMin} ${diffMin === 1 ? 'min' : 'mins'} ago`;
    }

    // Less than a day
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) {
      return `${diffHour} ${diffHour === 1 ? 'hr' : 'hrs'} ago`;
    }

    // Days
    const diffDay = Math.floor(diffHour / 24);
    return `${diffDay}d ago`;
  } catch {
    return 'Invalid Date';
  }
}
